import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Zap, TrendingUp, Target, Activity } from 'lucide-react';

const formatNumber = (value, digits = 0) => {
    if (!Number.isFinite(value)) return '—';
    if (Math.abs(value) >= 1_000_000) {
        return `${(value / 1_000_000).toFixed(digits)}M`;
    }
    if (Math.abs(value) >= 1_000) {
        return `${(value / 1_000).toFixed(digits)}K`;
    }
    return value.toFixed(digits);
};

const TierBadge = ({ tier }) => {
    const colors = {
        HIGH_PRIORITY: 'bg-red-100 text-red-700',
        STRONG_CANDIDATE: 'bg-purple-100 text-purple-700',
        POTENTIAL: 'bg-amber-100 text-amber-700',
        WATCH: 'bg-blue-100 text-blue-700'
    };
    return (
        <span className={`text-xs font-semibold px-2 py-1 rounded ${colors[tier] || 'bg-gray-100 text-gray-700'}`}>
            {tier || 'N/A'}
        </span>
    );
};

const RisingStarsTab = ({ onGameClick }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [summary, setSummary] = useState(null);
    const [results, setResults] = useState([]);
    const [analysis, setAnalysis] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [filters, setFilters] = useState({
        genres: 'Shooter,FPS,Action',
        minWishlists: 5000,
        minGrowthRate: 5,
        excludeAAA: true
    });

    useEffect(() => {
        fetchDashboard();
    }, []);

    const recomputeSummary = (list) => {
        const count = list.length;
        if (count === 0) {
            return {
                total: 0,
                avgVelocity: 0,
                avgGrowth: 0,
                avgWishlists: 0,
                tierCounts: {}
            };
        }
        const tierCounts = list.reduce((acc, g) => {
            const tier = g.partnershipRecommendation?.tier || 'WATCH';
            acc[tier] = (acc[tier] || 0) + 1;
            return acc;
        }, {});
        return {
            total: count,
            avgVelocity: Number((list.reduce((s, g) => s + (g.velocityScore || 0), 0) / count).toFixed(2)),
            avgGrowth: Number((list.reduce((s, g) => s + (g.growth?.wishlists?.percentage || 0), 0) / count).toFixed(2)),
            avgWishlists: Math.round(list.reduce((s, g) => s + (g.wishlists || 0), 0) / count),
            tierCounts
        };
    };

    const fetchDashboard = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch('/analytics/v2/rising-stars/dashboard');
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            const json = await res.json();
            const data = json.data || json;
            const list = data.results || data || [];
            const sum = data.summary || recomputeSummary(list);
            setResults(list);
            setSummary(sum);
            setAnalysis(null);
        } catch (err) {
            setError('Unable to load rising stars right now.');
            console.error('[RisingStars] dashboard error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (evt) => {
        evt?.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                genres: filters.genres,
                minWishlists: filters.minWishlists,
                minGrowthRate: filters.minGrowthRate,
                excludeAAA: filters.excludeAAA ? 'true' : 'false',
                unreleased: 'true',
                skipEnrichment: 'true'
            });
            const res = await fetch(`/analytics/v2/rising-stars/search?${params.toString()}`);
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            const json = await res.json();
            const list = json.data || json || [];
            setResults(list);
            setSummary(recomputeSummary(list));
            setAnalysis(null);
        } catch (err) {
            setError('Search failed. Try adjusting filters.');
            console.error('[RisingStars] search error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAnalyze = async (steamId) => {
        setAnalyzing(true);
        setAnalysis(null);
        try {
            const res = await fetch(`/analytics/v2/rising-stars/analyze/${steamId}`);
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            const json = await res.json();
            const data = json.data || json;
            setAnalysis(data);
        } catch (err) {
            setError('Failed to load analysis.');
            console.error('[RisingStars] analyze error:', err);
        } finally {
            setAnalyzing(false);
        }
    };

    const topRows = useMemo(() => results.slice(0, 15), [results]);

    const renderWishlistRecency = (wishlistAge) => {
        if (!wishlistAge || typeof wishlistAge !== 'object') return null;
        const order = ['<1m', '1-3m', '3-6m', '6m-1y', '1-2y', '2-3y', '3y+'];
        const entries = order
            .filter(key => wishlistAge[key] !== undefined)
            .map(key => ({ label: key, value: wishlistAge[key] }))
            .filter(item => Number.isFinite(item.value));
        if (entries.length === 0) return null;
        return (
            <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-500 uppercase">Wishlist recency</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {entries.map(({ label, value }) => {
                        const pct = Math.round(value * 1000) / 10; // one decimal
                        return (
                            <div key={label} className="bg-gray-50 rounded-md px-2 py-1 text-xs text-gray-700 flex items-center justify-between">
                                <span className="font-semibold">{label}</span>
                                <span className="text-blue-600 font-bold">{pct}%</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-gray-600">Loading Rising Stars…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-6 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5" />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-end gap-4">
                <div>
                    <h3 className="text-xl font-semibold text-gray-900">Rising Stars</h3>
                    <p className="text-gray-600">Unreleased shooters with momentum (snapshot-driven)</p>
                </div>
                <form onSubmit={handleSearch} className="flex flex-wrap gap-3 ml-auto items-end">
                    <div>
                        <label className="text-xs text-gray-500">Genres (comma)</label>
                        <input
                            className="border rounded px-3 py-2 text-sm"
                            value={filters.genres}
                            onChange={e => setFilters(f => ({ ...f, genres: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500">Min wishlists</label>
                        <input
                            type="number"
                            className="border rounded px-3 py-2 text-sm w-28"
                            value={filters.minWishlists}
                            onChange={e => setFilters(f => ({ ...f, minWishlists: Number(e.target.value) }))}
                        />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500">Min growth %</label>
                        <input
                            type="number"
                            className="border rounded px-3 py-2 text-sm w-24"
                            value={filters.minGrowthRate}
                            onChange={e => setFilters(f => ({ ...f, minGrowthRate: Number(e.target.value) }))}
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={filters.excludeAAA}
                            onChange={e => setFilters(f => ({ ...f, excludeAAA: e.target.checked }))}
                        />
                        Exclude AAA
                    </label>
                    <button
                        type="submit"
                        className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition"
                    >
                        Apply
                    </button>
                </form>
            </div>

            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <SummaryCard
                        icon={<Zap className="h-5 w-5" />}
                        title="Avg Velocity"
                        value={summary.avgVelocity}
                        suffix="/10"
                        color="text-indigo-600"
                    />
                    <SummaryCard
                        icon={<TrendingUp className="h-5 w-5" />}
                        title="Avg Wishlist Growth"
                        value={summary.avgGrowth}
                        suffix="%"
                        color="text-green-600"
                    />
                    <SummaryCard
                        icon={<Target className="h-5 w-5" />}
                        title="Avg Wishlists"
                        value={formatNumber(summary.avgWishlists)}
                        color="text-blue-600"
                    />
                    <SummaryCard
                        icon={<Activity className="h-5 w-5" />}
                        title="Candidates"
                        value={summary.total}
                        color="text-amber-600"
                    />
                </div>
            )}

            <div className="bg-white rounded-lg shadow p-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                        <tr className="text-left text-gray-600">
                            <th className="px-3 py-2">Game</th>
                            <th className="px-3 py-2">Velocity</th>
                            <th className="px-3 py-2">Tier</th>
                            <th className="px-3 py-2">Wishlists</th>
                            <th className="px-3 py-2">Growth</th>
                            <th className="px-3 py-2">Followers</th>
                            <th className="px-3 py-2">Genres</th>
                            <th className="px-3 py-2">Tags</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                    {topRows.map(game => (
                            <tr key={game.steamId} className="border-t border-gray-100">
                                <td className="px-3 py-2 font-medium text-gray-900">
                                    <button
                                        className="text-left hover:text-blue-600 font-semibold"
                                        onClick={() => onGameClick ? onGameClick(game) : handleAnalyze(game.steamId)}
                                    >
                                        {game.name}
                                    </button>
                                </td>
                                <td className="px-3 py-2 text-gray-700">{(game.velocityScore ?? 0).toFixed(1)}/10</td>
                                <td className="px-3 py-2"><TierBadge tier={game.partnershipRecommendation?.tier} /></td>
                                <td className="px-3 py-2 text-gray-900">{formatNumber(game.wishlists)}</td>
                                <td className="px-3 py-2 text-green-600">
                                    {game.growth?.wishlists?.percentage != null
                                        ? `+${game.growth.wishlists.percentage.toFixed(1)}%`
                                        : '—'}
                                </td>
                                <td className="px-3 py-2 text-gray-700">{formatNumber(game.followers)}</td>
                                <td className="px-3 py-2 text-gray-700">
                                    {game.genres && game.genres.length ? game.genres.slice(0, 3).join(', ') : '—'}
                                </td>
                                <td className="px-3 py-2 text-gray-700">
                                    {game.tags && game.tags.length ? game.tags.slice(0, 3).join(', ') : '—'}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <button
                                        className="text-blue-600 hover:text-blue-800 text-sm font-semibold"
                                        onClick={() => handleAnalyze(game.steamId)}
                                    >
                                        Analyze
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {topRows.length === 0 && (
                            <tr>
                                <td className="px-3 py-4 text-gray-500" colSpan={9}>No candidates found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {analysis && (
                <div className="bg-white rounded-lg shadow p-4 border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900">{analysis.name}</h4>
                        <TierBadge tier={analysis.partnershipRecommendation?.tier} />
                        <span className="text-sm text-gray-600">{(analysis.velocityScore ?? 0).toFixed(1)}/10</span>
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                        Wishlists: {formatNumber(analysis.wishlists)} | Followers: {formatNumber(analysis.followers)} | Growth: {analysis.growth?.wishlists?.percentage != null ? `+${analysis.growth.wishlists.percentage}%` : '—'}
                    </div>
                    <div className="mb-2">
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Insights</div>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                            {(analysis.analysis || []).map((line, idx) => (
                                <li key={idx}>{line}</li>
                            ))}
                        </ul>
                    </div>
                    {analysis.wishlistInsights?.wishlistAge && renderWishlistRecency(analysis.wishlistInsights.wishlistAge)}
                </div>
            )}

            {analyzing && (
                <div className="text-sm text-gray-500">Analyzing…</div>
            )}
        </div>
    );
};

const SummaryCard = ({ icon, title, value, suffix, color }) => (
    <div className="bg-white rounded-lg shadow p-4 flex items-center gap-3">
        <div className={`p-2 rounded-full bg-gray-100 ${color}`}>{icon}</div>
        <div>
            <div className="text-sm text-gray-500">{title}</div>
            <div className="text-xl font-semibold text-gray-900">
                {value != null ? value : '—'} {suffix || ''}
            </div>
        </div>
    </div>
);

export default RisingStarsTab;
